/**
 * SORRELLE · Root & Radiance PDP interactions.
 * Loaded (deferred) only by sections/sorrelle-pdp-theme.liquid.
 *
 * The offer selector does not add to cart itself. It writes the chosen variant id, quantity and
 * selling plan into Shrine's native product form, so loading states, errors and the cart drawer
 * keep working exactly as the theme ships them.
 */
(function () {
  'use strict';

  if (window.__sorrellePdpLoaded) return;
  window.__sorrellePdpLoaded = true;

  var reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  var inEditor = !!(window.Shopify && window.Shopify.designMode);

  /* ---------- Offer selector ---------- */

  class SorrelleOffers extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      this.sectionId = this.dataset.section;
      this.inputs = Array.prototype.slice.call(this.querySelectorAll('.sr-offer__input'));
      this.quantityInput = this.querySelector('[data-sr-quantity]');
      this.planInput = this.querySelector('[data-sr-selling-plan]');
      this.summary = this.querySelector('[data-sr-summary]');
      this.applying = false;

      this.addEventListener('change', this.onChange.bind(this));
      this.sync();
      this.observeButton();

      // Shrine may repaint the button after it finishes its own setup or a cart request.
      window.addEventListener('pageshow', this.sync.bind(this));
    }

    get form() {
      return document.getElementById(this.dataset.form);
    }

    get selected() {
      var checked = this.inputs.find(function (input) {
        return input.checked && !input.disabled;
      });
      return (
        checked ||
        this.inputs.find(function (input) {
          return !input.disabled;
        })
      );
    }

    onChange(event) {
      if (!event.target.classList || !event.target.classList.contains('sr-offer__input')) return;
      this.sync();
    }

    sync() {
      var input = this.selected;
      // Nothing purchasable: leave Shrine's own sold-out state untouched.
      if (!input) return;
      if (!input.checked) input.checked = true;

      this.inputs.forEach(function (candidate) {
        var card = candidate.closest('.sr-offer');
        if (card) card.classList.toggle('is-selected', candidate === input);
      });

      this.applying = true;

      var form = this.form;
      if (form) {
        var idInput = form.querySelector('input[name="id"]');
        if (idInput && idInput.value !== input.dataset.variantId) {
          idInput.value = input.dataset.variantId;
          idInput.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }

      if (this.quantityInput) this.quantityInput.value = input.dataset.quantity || '1';
      if (this.planInput) {
        var plan = input.dataset.sellingPlan || '';
        this.planInput.value = plan;
        // An empty selling plan is left out of the request entirely.
        this.planInput.disabled = plan === '';
      }

      this.paint(input);
      if (this.summary) this.summary.textContent = input.dataset.summary || '';
      this.applying = false;

      this.dispatchEvent(
        new CustomEvent('sorrelle:offer-change', {
          bubbles: true,
          detail: {
            offer: input.value,
            type: input.dataset.type,
            variantId: input.dataset.variantId,
            quantity: Number(input.dataset.quantity || 1),
            sellingPlan: input.dataset.sellingPlan || null
          }
        })
      );
    }

    /* Mirrors the selection into the native ATC button and the native sticky bar. */
    paint(input) {
      var id = this.sectionId;
      var label = input.dataset.ctaLabel;
      var price = input.dataset.priceFormatted;

      Array.prototype.forEach.call(document.querySelectorAll('.main-atc-price-span-' + id), function (el) {
        if (el.textContent !== price) el.textContent = price;
      });

      var button = document.getElementById('ProductSubmitButton-' + id);
      if (button && !button.hasAttribute('data-unavailable') && label) {
        var line = button.querySelector('.main-atc__label__line-1');
        var labelEl =
          line &&
          Array.prototype.find.call(line.children, function (el) {
            return (
              el.tagName === 'SPAN' &&
              !el.classList.contains('main-atc-price') &&
              !el.classList.contains('main-atc__label__icon')
            );
          });
        if (labelEl && labelEl.textContent.trim() !== label) labelEl.textContent = label;
      }

      var sticky = document.getElementById('sticky-atc-' + id);
      if (sticky) {
        var stickyButton = sticky.querySelector('.sticky-atc__add_button');
        var stickyLabel = sticky.querySelector('.sticky-atc__label__text');
        var stickySubtitle = sticky.querySelector('.sticky-atc__subtitle');
        if (stickyButton && label) stickyButton.dataset.label = label;
        if (stickyLabel && label && !(stickyButton && stickyButton.disabled) && stickyLabel.textContent.trim() !== label) {
          stickyLabel.textContent = label;
        }
        if (stickySubtitle && input.dataset.title && stickySubtitle.textContent !== input.dataset.title) {
          stickySubtitle.textContent = input.dataset.title;
        }
      }
    }

    /* Re-applies the label and price if the theme rewrites the button (for example after add to cart). */
    observeButton() {
      var self = this;
      var targets = [
        document.getElementById('ProductSubmitButton-' + this.sectionId),
        document.getElementById('sticky-atc-' + this.sectionId)
      ].filter(Boolean);
      if (!targets.length || !('MutationObserver' in window)) return;

      var scheduled = false;
      this.observer = new MutationObserver(function () {
        if (self.applying || scheduled) return;
        scheduled = true;
        window.requestAnimationFrame(function () {
          scheduled = false;
          var input = self.selected;
          if (!input) return;
          self.applying = true;
          self.paint(input);
          self.applying = false;
        });
      });
      targets.forEach(function (target) {
        self.observer.observe(target, { childList: true, characterData: true, subtree: true });
      });
    }

    disconnectedCallback() {
      if (this.observer) this.observer.disconnect();
    }
  }

  if (!customElements.get('sorrelle-offers')) {
    customElements.define('sorrelle-offers', SorrelleOffers);
  }

  /* Ignore a second submit fired within a moment of the first (double click / double tap). */
  var lastSubmit = 0;
  document.addEventListener(
    'submit',
    function (event) {
      var form = event.target;
      // getAttribute, because the product form's <input name="id"> shadows form.id.
      var formId = form && form.getAttribute ? form.getAttribute('id') : null;
      if (!formId || !document.querySelector('sorrelle-offers[data-form="' + formId + '"]')) return;
      var now = Date.now();
      if (now - lastSubmit < 900) {
        event.preventDefault();
        event.stopImmediatePropagation();
        return;
      }
      lastSubmit = now;
    },
    true
  );

  /* ---------- Placeholder gallery (only rendered while the product has no media) ---------- */

  class SorrelleShotlist extends HTMLElement {
    connectedCallback() {
      if (this.initialised) return;
      this.initialised = true;

      var track = this.querySelector('[data-sr-track]');
      var thumbs = Array.prototype.slice.call(this.querySelectorAll('[data-sr-thumb]'));
      var slides = Array.prototype.slice.call(this.querySelectorAll('[data-sr-slide]'));
      if (!track || !slides.length) return;

      function setActive(id) {
        thumbs.forEach(function (thumb) {
          if (thumb.dataset.srThumb === id) thumb.setAttribute('aria-current', 'true');
          else thumb.removeAttribute('aria-current');
        });
      }

      thumbs.forEach(function (thumb) {
        thumb.addEventListener('click', function () {
          var slide = document.getElementById(thumb.dataset.srThumb);
          if (!slide) return;
          track.scrollTo({ left: slide.offsetLeft - track.offsetLeft, behavior: reduceMotion ? 'auto' : 'smooth' });
          setActive(slide.id);
        });
      });

      if ('IntersectionObserver' in window) {
        var observer = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              if (entry.isIntersecting) setActive(entry.target.id);
            });
          },
          { root: track, threshold: 0.6 }
        );
        slides.forEach(function (slide) {
          observer.observe(slide);
        });
      }
      setActive(slides[0].id);
    }
  }

  if (!customElements.get('sorrelle-shotlist')) {
    customElements.define('sorrelle-shotlist', SorrelleShotlist);
  }

  /* ---------- Anchor buttons back to the purchase box ---------- */

  document.addEventListener('click', function (event) {
    var link = event.target.closest && event.target.closest('a[href="#sorrelle-buy-box"]');
    if (!link) return;
    var box = document.getElementById('sorrelle-buy-box');
    if (!box) return;
    event.preventDefault();
    box.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'center' });
    var target = box.querySelector('.sr-offer__input:checked') || box.querySelector('.sr-offer__input:not(:disabled)');
    if (target) {
      window.setTimeout(
        function () {
          target.focus({ preventScroll: true });
        },
        reduceMotion ? 0 : 500
      );
    }
  });

  /* ---------- Supplement Facts: shorter table on phones ----------
     The whole table stays in the page (and readable by assistive technology); the stylesheet only
     limits its height below the desktop breakpoint until the customer expands it. */

  function initCollapse() {
    Array.prototype.forEach.call(document.querySelectorAll('[data-sr-collapse-toggle]'), function (toggle) {
      var target = document.getElementById(toggle.getAttribute('aria-controls'));
      if (!target || toggle.dataset.srBound) return;
      toggle.dataset.srBound = 'true';
      toggle.hidden = false;
      target.classList.add('is-collapsed');
      toggle.addEventListener('click', function () {
        var expanded = target.classList.toggle('is-collapsed') === false;
        toggle.setAttribute('aria-expanded', expanded ? 'true' : 'false');
        var label = toggle.querySelector('[data-sr-collapse-label]');
        if (label) label.textContent = expanded ? toggle.dataset.labelLess : toggle.dataset.labelMore;
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initCollapse);
  } else {
    initCollapse();
  }
  document.addEventListener('shopify:section:load', initCollapse);

  /* ---------- Gentle reveal on scroll ---------- */

  function initReveal() {
    if (inEditor || reduceMotion || !('IntersectionObserver' in window)) return;
    var items = document.querySelectorAll('.sr-reveal');
    if (!items.length) return;

    document.documentElement.classList.add('sr-js');
    var observer = new IntersectionObserver(
      function (entries) {
        entries.forEach(function (entry) {
          if (!entry.isIntersecting) return;
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        });
      },
      { rootMargin: '0px 0px -6% 0px', threshold: 0.08 }
    );
    Array.prototype.forEach.call(items, function (item) {
      observer.observe(item);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initReveal);
  } else {
    initReveal();
  }
})();
